import { getI18n } from "react-i18next";

import axios from "axios";
import { ERROR_CODES, ROUTES } from "@/shared";
import { useErrorStore } from "@/stores/useErrorStore";
import { useNavigationStore } from "@/stores/useNavigationStore";
import { useTokenStore } from "@/stores/useTokenStore";

import { ApiResources } from "./resources";

const axiosDefault = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL
});

export default axiosDefault;
export const abortController = new AbortController();
export const isCanceled = axios.isCancel;

export const axiosPrivate = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    accept: "*/*"
  },
  withCredentials: true
});

let refreshingFunc: any = undefined;

axiosPrivate.interceptors.request.use(
  async (config: any) => {
    const currentTokenInfo = useTokenStore.getState().tokenInfo;
    config.headers["Authorization"] ??= `Bearer ${currentTokenInfo?.token}`;
    return config;
  },
  error =>
    Promise.reject(error instanceof Error ? error : new Error(String(error)))
);

const refreshTokens = async () => {
  if (!refreshingFunc) {
    const currentTokenInfo = useTokenStore.getState().tokenInfo;
    refreshingFunc = axiosDefault.post(ApiResources.RefreshToken, {
      accessToken: currentTokenInfo?.token,
      refreshToken: currentTokenInfo?.refreshToken
    });
  }
  return await refreshingFunc;
};

const updateRequestTokens = (
  originalRequest: any,
  accessToken: string,
  refreshToken: string
) => {
  originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;

  if (!originalRequest.data) return;

  try {
    const parsedData = JSON.parse(originalRequest.data);
    if (parsedData.accessToken && parsedData.refreshToken) {
      parsedData.accessToken = accessToken;
      parsedData.refreshToken = refreshToken;
      originalRequest.data = JSON.stringify(parsedData);
    }
  } catch (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }
};

const handleCompanyChangeRoute = (originalRequest: any) => {
  if (originalRequest?.url === ApiResources.ChangeSelectedCompany) {
    const targetRoute = useNavigationStore.getState().targetRoute;
    targetRoute && useNavigationStore.getState().setPendingRoute(targetRoute);
  }
};

const handleTokenRefresh = async (originalRequest: any) => {
  try {
    const resp = await refreshTokens();

    if (!resp?.data) return;

    useTokenStore.getState().setTokenInfo(resp.data);
    useErrorStore.getState().setIsServerError?.(undefined);

    updateRequestTokens(
      originalRequest,
      resp.data.token,
      resp.data.refreshToken
    );
    handleCompanyChangeRoute(originalRequest);

    return axiosPrivate(originalRequest);
  } catch (err: any) {
    const status = err?.response?.status;

    useTokenStore.getState().setTokenInfo(null);
    useNavigationStore.getState().setRedirectRoute(ROUTES.Home);

    if (
      status === ERROR_CODES.Unauthorized ||
      status === ERROR_CODES.BadRequest
    ) {
      throw new Error(getI18n().t("Alert##session expired"), { cause: err });
    } else {
      throw err instanceof Error ? err : new Error(String(err));
    }
  } finally {
    refreshingFunc = undefined;
  }
};

const handleSpecificErrors = (error: any) => {
  if (error?.response?.status === ERROR_CODES.InternalServerError) {
    useErrorStore.getState().setIsServerError?.(error);
  }

  if (error?.message === "Network Error") {
    useErrorStore.getState().setIsNetworkError?.(error);
  }
};

axiosPrivate.interceptors.response.use(
  response => response,
  async (error: any) => {
    const originalRequest = error?.config;
    const isUnauthorized = error?.response?.status === ERROR_CODES.Unauthorized;
    const shouldRetry = isUnauthorized && !originalRequest._retry;

    if (shouldRetry) {
      originalRequest._retry = true;
      const result = await handleTokenRefresh(originalRequest);
      if (result) return result;
    }

    handleSpecificErrors(error);

    throw error instanceof Error ? error : new Error(String(error));
  }
);
